import {MISC_KEYS} from "@/api/miscApi";
import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import {resetAllStores} from "@/store/createStore";
import {
  hasClosedPaymentFailedModalAtom,
  lastSelectedGroupIdAtom,
  latestActiveCampaignForInviteAtom,
  locationAtom,
  selectedCompanyIdAtom,
} from "@/store/jotaiAtoms";
import useUserStore from "@/store/user";
import {getDefaultStore} from "jotai";
import Cookies from "js-cookie";
import jwt_decode from "jwt-decode";
import {create} from "zustand";
import {devtools, persist} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

type TState = {
  access: string | null;
  refresh: string | null;
  userId: number | null;
  setAccessToken: (token: string | null) => void;
  setRefreshToken: (token: string | null) => void;
  logout(): Promise<void>;
  reset(): void;
};

const initialState = {
  access: null,
  refresh: null,
  userId: null,
};

export const useTokenStore = create<TState>()(
  persist(
    immer(
      devtools(
        (set, get) => ({
          ...initialState,
          setAccessToken: accessToken => {
            const decodedToken = jwt_decode(accessToken ?? "");
            set(
              state => {
                state.access = accessToken;
                if (
                  decodedToken &&
                  typeof decodedToken === "object" &&
                  "user_id" in decodedToken &&
                  decodedToken.user_id &&
                  typeof decodedToken.user_id === "number"
                )
                  state.userId = decodedToken.user_id;
              },
              false,
              "useTokenStore/setAccessToken",
            );
          },
          setRefreshToken: refreshToken =>
            set(
              state => {
                state.refresh = refreshToken;
              },
              false,
              "useTokenStore/setRefreshToken",
            ),
          logout: async () => {
            if (!get().access) return;

            try {
              await client.post(
                MISC_KEYS.mTokenLogout[0],
                {access: get().access},
                {
                  headers: {
                    Authorization: `Bearer ${get().access}`,
                  },
                },
              );
            } catch (error: any) {
              console.log("Error during logout\n", error.response?.data?.detail);
            }

            queryClient.clear();
            get().reset();
            useUserStore.getState().reset();
            Cookies.remove("isLoggedIn");
            resetAllStores();
            getDefaultStore().set(hasClosedPaymentFailedModalAtom, false);
            getDefaultStore().set(latestActiveCampaignForInviteAtom, null);
            getDefaultStore().set(selectedCompanyIdAtom, null);
            getDefaultStore().set(lastSelectedGroupIdAtom, null);
            getDefaultStore().set(locationAtom, {previousRoute: "", currentRoute: ""});
          },
          reset: () => {
            set(() => ({...initialState}), false, "useTokenStore/reset");
            localStorage.removeItem("token-storage");
          },
        }),
        {name: "useTokenStore"},
      ),
    ),
    {name: "token-store"},
  ),
);

export default useTokenStore;
